import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Loader2, Shield, User as UserIcon, Edit2 } from 'lucide-react';
import { Button } from "../../components/ui/button";
import moment from 'moment';

interface User {
  id: string;
  email: string;
  name?: string;
  isAdmin: boolean;
  status: 'ACTIVE' | 'BANNED' | 'DELETED';
  isEmailVerified: boolean;
  tier: 'FREE' | 'BASIC' | 'PRO' | 'AGENCY';
  storageUsed: number;
  totalSpent: number;
  lastManualStatsUpdate?: string;
  createdAt: string;
}

export default function UsersTable() {
  const token = localStorage.getItem('token');
  const history = useHistory();

  const { data: users, isLoading, error } = useQuery<User[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });


  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
      case 'BANNED':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Banned</Badge>;
      case 'DELETED':
        return <Badge className="bg-slate-500/20 text-muted-foreground border-slate-500/30">Deleted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4 bg-red-500/10 rounded-xl border border-red-500/20">
        Error loading users: {(error as any).message}
      </div>
    );
  }

  return (
    <Card className="bg-card border-border text-foreground shadow-2xl">
      <CardHeader>
        <CardTitle className="text-2xl font-black flex items-center gap-2">
          <UserIcon className="h-6 w-6 text-primary" />
          Platform Users
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader className="bg-card/50">
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">User</TableHead>
                <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Email</TableHead>
                <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Tier</TableHead>
                <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Role</TableHead>
                <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Status</TableHead>
                <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Spent</TableHead>
                <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Joined</TableHead>
                <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users?.map((user) => (
                <TableRow key={user.id} className="border-border hover:bg-card/30 transition-colors">
                  <TableCell className="font-medium text-foreground">{user.name || 'Anonymous'}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {user.email}
                    {user.isEmailVerified ? (
                      <Badge className="ml-2 bg-primary/10 text-primary border-primary/20 text-[8px] h-4 py-0">Verified</Badge>
                    ) : (
                      <Badge className="ml-2 bg-amber-500/10 text-amber-400 border-amber-500/20 text-[8px] h-4 py-0">Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      user.tier === 'FREE' ? "bg-slate-500/20 text-muted-foreground border-slate-500/30 text-[8px]" :
                        user.tier === 'BASIC' ? "bg-primary/20 text-primary border-primary/30 text-[8px]" :
                          user.tier === 'PRO' ? "bg-purple-500/20 text-purple-400 border-purple-500/30 text-[8px]" :
                            "bg-amber-500/20 text-amber-400 border-amber-500/30 text-[8px]"
                    }>
                      {user.tier === 'PRO' ? 'INDIE' : user.tier}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.isAdmin ? (
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 gap-1">
                        <Shield className="h-3 w-3" />
                        Admin
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-border text-muted-foreground">
                        User
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(user.status)}
                  </TableCell>
                  <TableCell className="text-green-400 font-black font-mono text-xs">
                    ${user.totalSpent.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {moment(user.createdAt).format('LL')}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => history.push(`/admin/users/${user.id}`)}
                      className="text-primary hover:text-primary/80"
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
